import { Link } from 'react-router-dom';
import { CheckIcon } from 'lucide-react';
import { ProductImage } from '../../ui/ProductImage';
import { useCartActions } from '../../contexts/StoreContext';
import { useCatalog } from '../../contexts/CatalogContext';
import type { Routine } from '../../core/types/catalog';
import { euro } from '../../core/format/currency';
import { listNl } from '../../core/format/text';
import { countSoldOut, isAllSoldOut, isSoldOut } from '../../core/rules/stock';
import { shopLabels } from '../../data/shopLabels';
import { btnPrice, btnPriceAmount, btnPriceLabel } from '../../ui/styles';

/**
 * Protocolkaart (home, webshop, protocollen): claim, naam, bij welke klachten, verhaal, de
 * producten, wat erbij zit, en één knop met de prijs erin. De klachten komen uit de koppeling
 * in Aanbod › Huidklachten, zodat je zonder lezen ziet of dit voor jou is.
 */
export function RoutineCard({
  routine
}: {
  routine: Routine;
}) {
  const {
    addRoutine
  } = useCartActions();
  const {
    routineTotals,
    treatmentForRoutine,
    concerns
  } = useCatalog();
  const {
    items,
    price,
    months
  } = routineTotals(routine);
  const treatment = treatmentForRoutine(routine);
  const soldOutCount = countSoldOut(items);
  const allSoldOut = isAllSoldOut(items);
  const forConcerns = concerns.filter(c => c.routineId === routine.id).map(c => c.label.toLowerCase());
  return <article className="flex h-full flex-col" aria-labelledby={`routine-${routine.id}`} data-id="element-8729" data-mp="3atbrs5ly18gx">
      <div className="flex items-start justify-between gap-3" data-id="element-8730" data-mp="2drzp9x0dw3g">
        <p className="font-display text-[15px] italic text-clay" data-id="element-8731" data-mp="7cmiwcyw1mo3">{routine.claim}</p>
        {routine.level === 'Compleet' && <span className="whitespace-nowrap rounded-full bg-clay px-3 py-1 text-[12px] text-shell" data-id="element-8732" data-mp="3os1ovl8rj85a">Meest compleet</span>}
      </div>
      <h3 id={`routine-${routine.id}`} className="mt-2 font-display text-[1.75rem] font-light leading-tight text-ink" data-id="element-8733" data-mp="3tpuuac28ry4i">
        {routine.name}
      </h3>
      {forConcerns.length > 0 && <p className="mt-1.5 text-[14px] text-ink" data-id="element-8734" data-mp="q4fb4564v3ur">Bij {listNl(forConcerns)}</p>}
      <p className="mt-3 text-[15px] leading-relaxed text-muted" data-id="element-8735" data-mp="zxs0f1wgdcrx">{routine.story}</p>

      <ul className="mt-5 divide-y divide-line border-y border-line" aria-label={`Producten in ${routine.name}`} data-id="element-8736" data-mp="uzgp0mqieu7l">
        {items.map(p => <li key={p.id} className="flex items-center gap-3 py-3" data-id="element-8737" data-mp="pyu3pbovicxu">
            <ProductImage src={p.image} alt="" width={40} className="h-12 w-10 shrink-0 p-1" data-id="element-8738" data-mp="ly0feh7iqw4f" />
            <div className="min-w-0 flex-1" data-id="element-8739" data-mp="h10j2y0h17qi">
              <p className="text-[12px] text-clay" data-id="element-8740" data-mp="h21s6wxxfk5l">{isSoldOut(p) ? shopLabels.soldOutShort : p.step}</p>
              <Link to={`/webshop/${p.id}`} className="text-[15px] leading-snug text-ink transition-colors duration-150 ease-soft hover:text-clay" data-id="element-8741" data-mp="c7ficy7htznm">
                {p.name}
              </Link>
            </div>
            <span className="text-[14px] tabular-nums text-muted" data-id="element-8742" data-mp="3uevgps8o48ge">{euro(p.price)}</span>
          </li>)}
      </ul>

      {routine.included.length > 0 && <ul className="mt-4 space-y-1.5" aria-label="Inbegrepen" data-id="element-8743" data-mp="3pgk8fly0u60a">
          {routine.included.map(inc => <li key={inc} className="flex items-start gap-2 text-[14px] leading-snug text-muted" data-id="element-8744" data-mp="24zu7cjhnm6fk">
              <CheckIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-sage" strokeWidth={2} aria-hidden="true" data-id="element-8745" data-mp="29yxdy8da8kpn" />
              {inc}
            </li>)}
        </ul>}

      <div className="mt-auto pt-6" data-id="element-8746" data-mp="2iwzc8g5msz98">
        <p className="mb-3 text-[13px] text-muted" data-id="element-8747" data-mp="1m78iwk92ti50">Gaat {months} mee</p>
        <button type="button" onClick={() => addRoutine(routine.id)} disabled={allSoldOut} aria-label={allSoldOut ? `${routine.name} is tijdelijk uitverkocht` : `${routine.name} in winkelmand, ${euro(price)}`} className={btnPrice} data-id="element-8748" data-mp="1m932nv8m347i">
          {allSoldOut ? <span className="w-full text-center" data-id="element-8749" data-mp="200hk3xmvexcg">{shopLabels.soldOutShort}</span> : <>
              <span className={btnPriceLabel} data-id="element-8750" data-mp="17i2k2xe5htxn">Protocol in de mand</span>
              <span className={btnPriceAmount} data-id="element-8751" data-mp="17g9b3bg2exmh">{euro(price)}</span>
            </>}
        </button>
        {soldOutCount > 0 && !allSoldOut && <p className="mt-3 text-center text-[13px] text-muted" data-id="element-8752" data-mp="356pkpm3avbs0">{shopLabels.partlySoldOut(soldOutCount)}</p>}
        {treatment && <p className="mt-3 text-center text-[13px] text-muted" data-id="element-8753" data-mp="3iy4feq6kl1ew">Het thuisprotocol bij {treatment.name}</p>}
      </div>
    </article>;
}